import * as RD from '@radix-ui/react-dialog';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export interface AnchorBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}
function clippedAnchor(anchor: HTMLElement): AnchorBox | null {
  if (!anchor.isConnected || !anchor.getClientRects().length) return null;
  const r = anchor.getBoundingClientRect();
  const box: AnchorBox = {
    left: Math.max(0, r.left),
    right: Math.min(window.innerWidth, r.right),
    top: Math.max(0, r.top),
    bottom: Math.min(window.innerHeight, r.bottom),
  };
  for (let parent = anchor.parentElement; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent),
      p = parent.getBoundingClientRect();
    if (style.overflowX !== 'visible') {
      box.left = Math.max(box.left, p.left + parent.clientLeft);
      box.right = Math.min(box.right, p.left + parent.clientLeft + parent.clientWidth);
    }
    if (style.overflowY !== 'visible') {
      box.top = Math.max(box.top, p.top + parent.clientTop);
      box.bottom = Math.min(box.bottom, p.top + parent.clientTop + parent.clientHeight);
    }
  }
  return box.right > box.left && box.bottom > box.top ? box : null;
}
/** Немодальный слой на существующем Dialog: клавиатура и focus scope принадлежат Radix. */
export function Popover({
  id,
  open,
  anchor,
  label,
  onOpenChange,
  children,
  returnFocus = true,
  getAnchorRect,
  initialFocus,
}: {
  id: string;
  open: boolean;
  anchor: HTMLElement | null;
  label: string;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  returnFocus?: boolean;
  getAnchorRect?: () => AnchorBox | null;
  /** Opt-in focus target; other consumers retain their current focus order. */
  initialFocus?: () => HTMLElement | null;
}) {
  const [surface, setSurface] = useState<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const fallbackWidth = Math.max(
      1,
      Math.min(320, typeof window === 'undefined' ? 320 : window.innerWidth - 16),
    ),
    fallbackHeight = Math.max(1, typeof window === 'undefined' ? 600 : window.innerHeight - 16);
  const lastAnchor = useRef<HTMLElement | null>(null),
    outside = useRef(false),
    initialFocused = useRef(false);
  useLayoutEffect(() => {
    if (!open || !anchor || !surface) return;
    lastAnchor.current = anchor;
    outside.current = false;
    let frame = 0;
    const measure = () => {
      if (!anchor.isConnected) {
        outside.current = true;
        onOpenChange(false);
        return;
      }
      const box = getAnchorRect ? getAnchorRect() : clippedAnchor(anchor);
      if (!box || box.right <= box.left || box.bottom <= box.top) {
        outside.current = true;
        onOpenChange(false);
        return;
      }
      const width = Math.max(1, Math.min(320, window.innerWidth - 16)),
        maxHeight = Math.max(1, window.innerHeight - 16);
      const height = Math.min(surface.offsetHeight, maxHeight);
      const left = Math.max(8, Math.min(box.left, window.innerWidth - width - 8));
      const below = box.bottom + 8,
        above = box.top - height - 8;
      const top = Math.max(
        8,
        Math.min(
          below + height <= window.innerHeight - 8 ? below : above >= 8 ? above : below,
          window.innerHeight - height - 8,
        ),
      );
      setPosition((previous) =>
        previous &&
        previous.left === left &&
        previous.top === top &&
        previous.width === width &&
        previous.maxHeight === maxHeight
          ? previous
          : { left, top, width, maxHeight },
      );
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(anchor);
    observer.observe(surface);
    for (let parent = anchor.parentElement; parent; parent = parent.parentElement) observer.observe(parent);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    measure();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [open, anchor, surface, getAnchorRect, onOpenChange]);
  useLayoutEffect(() => {
    if (!open) {
      initialFocused.current = false;
      return;
    }
    if (!initialFocus || !surface || !position || initialFocused.current) return;
    let frame = 0;
    const focusWhenVisible = () => {
      if (!surface.isConnected) return;
      const target = initialFocus();
      if (!target || !surface.contains(target)) return;
      if (getComputedStyle(target).visibility !== 'visible') {
        frame = requestAnimationFrame(focusWhenVisible);
        return;
      }
      target.focus({ preventScroll: true });
      initialFocused.current = document.activeElement === target;
    };
    frame = requestAnimationFrame(focusWhenVisible);
    return () => cancelAnimationFrame(frame);
  }, [open, surface, position, initialFocus]);
  return (
    <RD.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <RD.Portal>
        <RD.Content
          ref={setSurface}
          id={id}
          data-slot="popover"
          aria-describedby={undefined}
          className="fixed z-popover max-w-full animate-pop-in overflow-y-auto rounded-lg border border-line bg-elevated text-fg shadow-lg motion-reduce:animate-none"
          style={{
            left: position?.left ?? 0,
            top: position?.top ?? 0,
            width: position?.width ?? fallbackWidth,
            maxHeight: position?.maxHeight ?? fallbackHeight,
            visibility: position ? 'visible' : 'hidden',
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (initialFocus) return;
            const node = event.target;
            requestAnimationFrame(() => {
              if (!(node instanceof HTMLElement) || !node.isConnected) return;
              const first = node.querySelector<HTMLElement>(
                'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]',
              );
              (first ?? node).focus({ preventScroll: true });
            });
          }}
          onInteractOutside={(event) => {
            if (event.target instanceof Node && lastAnchor.current?.contains(event.target)) {
              event.preventDefault();
              return;
            }
            outside.current = true;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (returnFocus && !outside.current && lastAnchor.current?.isConnected)
              lastAnchor.current.focus({ preventScroll: true });
            outside.current = false;
          }}
        >
          <RD.Title className="sr-only">{label}</RD.Title>
          {children}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
